/* eslint-disable */
import React from 'react';
import { Link } from 'react-router-dom';
import SEO from './SEO';
import './styles/styles.css';

const berkeleySchema = {
  '@context': 'https://schema.org',
  '@type': 'LocalBusiness',
  name: 'Bins Storage — Berkeley',
  description:
    'On-demand valet storage in Berkeley, CA. Free pickup and delivery from your door. No truck needed. Serving Berkeley, North Berkeley, Southside, West Berkeley, and Richmond.',
  url: 'https://bins-storage.com/berkeley',
  areaServed: [
    { '@type': 'City', 'name': 'Berkeley' },
    { '@type': 'City', 'name': 'Richmond' },
    { '@type': 'Neighborhood', 'name': 'Southside Berkeley' },
    { '@type': 'Neighborhood', 'name': 'North Berkeley' },
    { '@type': 'Neighborhood', 'name': 'West Berkeley' },
  ],
  offers: [
    {
      '@type': 'Offer',
      name: '5x5 Storage Unit',
      price: '30',
      priceCurrency: 'USD',
    },
    {
      '@type': 'Offer',
      name: '5x10 Storage Unit',
      price: '50',
      priceCurrency: 'USD',
    },
  ],
};

const faqSchema = {
  '@context': 'https://schema.org',
  '@type': 'FAQPage',
  mainEntity: [
    {
      '@type': 'Question',
      name: 'Does Bins Storage serve Berkeley, CA?',
      acceptedAnswer: {
        '@type': 'Answer',
        text: 'Yes. Bins Storage serves Berkeley and surrounding areas including Richmond, Southside, North Berkeley, and West Berkeley. We offer free pickup from your door.',
      },
    },
    {
      '@type': 'Question',
      name: 'How much does valet storage cost in Berkeley?',
      acceptedAnswer: {
        '@type': 'Answer',
        text: 'Bins offers a 5x5 unit starting at $30/month and a 5x10 unit starting at $50/month, with free initial pickup included.',
      },
    },
    {
      '@type': 'Question',
      name: 'Do I need a truck or car to use Bins Storage in Berkeley?',
      acceptedAnswer: {
        '@type': 'Answer',
        text: 'No. Bins handles all pickup and delivery. A driver comes to your door to collect your items and returns them on demand — no truck, no driving required.',
      },
    },
    {
      '@type': 'Question',
      name: 'Can UC Berkeley students use Bins Storage?',
      acceptedAnswer: {
        '@type': 'Answer',
        text: "Yes. Bins serves both on-campus and off-campus locations in Berkeley. It's especially popular for summer storage when students leave their dorms or apartments.",
      },
    },
  ],
};

const combinedSchema = [berkeleySchema, faqSchema];

// ─── styles ───────────────────────────────────────────────────────────────────

const heroStyle = {
  backgroundColor: '#7B1FA2',
  padding: '4rem 2rem 3rem',
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  textAlign: 'center',
};

const heroHeadingStyle = {
  fontFamily: 'Raleway, sans-serif',
  fontSize: 'clamp(2rem, 5vw, 3.5rem)',
  fontWeight: 700,
  color: '#FFF',
  margin: '0 0 1rem',
};

const heroSubStyle = {
  fontFamily: 'Raleway, sans-serif',
  fontSize: '1.2rem',
  color: 'rgba(255,255,255,0.88)',
  maxWidth: 600,
  lineHeight: 1.6,
  margin: '0 0 2rem',
};

const ctaButton = {
  display: 'inline-block',
  backgroundColor: '#FFF',
  color: '#7B1FA2',
  fontFamily: 'Raleway, sans-serif',
  fontWeight: 700,
  fontSize: '1.1rem',
  padding: '14px 32px',
  borderRadius: 999,
  textDecoration: 'none',
  marginBottom: '0.5rem',
};

const sectionStyle = {
  padding: '3.5rem 2rem',
  maxWidth: 860,
  margin: '0 auto',
};

const sectionHeadingStyle = {
  fontFamily: 'Raleway, sans-serif',
  fontSize: '2rem',
  fontWeight: 700,
  color: '#261136',
  marginBottom: '0.5rem',
  textAlign: 'center',
};

const sectionSubStyle = {
  fontFamily: 'Raleway, sans-serif',
  fontSize: '1.05rem',
  color: '#555',
  textAlign: 'center',
  maxWidth: 640,
  margin: '0 auto 2.5rem',
  lineHeight: 1.65,
};

const stepsGrid = {
  display: 'grid',
  gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))',
  gap: '1.5rem',
};

const stepCard = {
  backgroundColor: '#F9F6FC',
  borderRadius: 12,
  padding: '1.75rem 1.5rem',
  textAlign: 'center',
};

const stepNumber = {
  fontFamily: 'Raleway, sans-serif',
  fontSize: '2.5rem',
  fontWeight: 800,
  color: '#7B1FA2',
  marginBottom: '0.5rem',
};

const stepTitle = {
  fontFamily: 'Raleway, sans-serif',
  fontSize: '1.1rem',
  fontWeight: 700,
  color: '#261136',
  marginBottom: '0.4rem',
};

const stepBody = {
  fontFamily: 'Raleway, sans-serif',
  fontSize: '0.95rem',
  color: '#555',
  lineHeight: 1.6,
};

const pricingSection = {
  backgroundColor: '#261136',
  padding: '3.5rem 2rem',
  textAlign: 'center',
};

const pricingHeading = {
  fontFamily: 'Raleway, sans-serif',
  fontSize: '2rem',
  fontWeight: 700,
  color: '#FFF',
  marginBottom: '0.5rem',
};

const pricingSubStyle = {
  fontFamily: 'Raleway, sans-serif',
  fontSize: '1.05rem',
  color: '#AAB5E0',
  marginBottom: '2rem',
};

const pricingGrid = {
  display: 'flex',
  flexWrap: 'wrap',
  gap: '1.5rem',
  justifyContent: 'center',
  maxWidth: 700,
  margin: '0 auto 2rem',
};

const pricingCard = {
  background: 'radial-gradient(circle at top, #3A1F52 0, #170824 60%, #12061f 100%)',
  borderRadius: 16,
  padding: '2rem 1.75rem',
  border: '1px solid rgba(255,255,255,0.1)',
  color: '#F5F5FA',
  minWidth: 240,
  flex: '1 1 240px',
  maxWidth: 300,
  textAlign: 'left',
};

const pricingLabel = {
  fontFamily: 'Raleway, sans-serif',
  fontSize: '0.85rem',
  color: '#AAB5E0',
  letterSpacing: '0.06em',
  textTransform: 'uppercase',
  marginBottom: 8,
};

const pricingPrice = {
  fontFamily: 'Raleway, sans-serif',
  fontSize: '2.25rem',
  fontWeight: 800,
  color: '#FFF',
  marginBottom: 4,
};

const pricingDesc = {
  fontFamily: 'Raleway, sans-serif',
  fontSize: '0.9rem',
  color: '#AAB5E0',
  lineHeight: 1.6,
};

const pricingCtaStyle = {
  display: 'inline-block',
  backgroundColor: '#7B1FA2',
  color: '#FFF',
  fontFamily: 'Raleway, sans-serif',
  fontWeight: 700,
  fontSize: '1rem',
  padding: '12px 28px',
  borderRadius: 999,
  textDecoration: 'none',
  border: '2px solid rgba(255,255,255,0.3)',
};

const areaSection = {
  backgroundColor: '#F9F6FC',
  padding: '3rem 2rem',
  textAlign: 'center',
};

const areaGrid = {
  display: 'flex',
  flexWrap: 'wrap',
  gap: '0.75rem',
  justifyContent: 'center',
  maxWidth: 700,
  margin: '1.5rem auto 0',
};

const areaBadge = {
  backgroundColor: '#EDE0F7',
  color: '#5B1F8A',
  fontFamily: 'Raleway, sans-serif',
  fontWeight: 600,
  fontSize: '0.9rem',
  padding: '8px 18px',
  borderRadius: 999,
};

const faqSection = {
  padding: '3.5rem 2rem',
  maxWidth: 720,
  margin: '0 auto',
};

const faqItem = {
  borderBottom: '1px solid #EEE',
  paddingBottom: '1.25rem',
  marginBottom: '1.25rem',
};

const faqQuestion = {
  fontFamily: 'Raleway, sans-serif',
  fontWeight: 700,
  fontSize: '1.05rem',
  color: '#261136',
  marginBottom: '0.4rem',
};

const faqAnswer = {
  fontFamily: 'Raleway, sans-serif',
  fontSize: '0.97rem',
  color: '#555',
  lineHeight: 1.65,
};

const crossLinkBanner = {
  backgroundColor: '#7B1FA2',
  padding: '2.5rem 2rem',
  textAlign: 'center',
};

const crossLinkText = {
  fontFamily: 'Raleway, sans-serif',
  fontSize: '1.15rem',
  color: 'rgba(255,255,255,0.9)',
  marginBottom: '1.25rem',
  textAlign: 'center',
};

const crossLinkButton = {
  display: 'inline-block',
  backgroundColor: 'transparent',
  color: '#FFF',
  fontFamily: 'Raleway, sans-serif',
  fontWeight: 700,
  fontSize: '1rem',
  padding: '11px 26px',
  borderRadius: 999,
  textDecoration: 'none',
  border: '2px solid rgba(255,255,255,0.6)',
};

// ─── component ────────────────────────────────────────────────────────────────

const Berkeley = () => (
  <>
    <SEO
      title="Valet Storage Berkeley CA | Free Pickup & Delivery | Bins Storage"
      description="On-demand valet storage in Berkeley, CA starting at $30/month. Free pickup from your door — no truck needed. Serving UC Berkeley, Southside, North Berkeley, and Richmond."
      path="/berkeley"
      schema={combinedSchema}
    />

    {/* HERO */}
    <section style={heroStyle}>
      <h1 style={heroHeadingStyle}>
        Valet Storage in Berkeley, CA
      </h1>
      <p style={heroSubStyle}>
        We pick up, store, and deliver your belongings from your door — no truck, no
        driving, no hassle. Starting at $30/month with free pickup included.
      </p>
      <a href="/Download" style={ctaButton}>Reserve a Unit</a>
      <p style={{ fontFamily: 'Raleway, sans-serif', fontSize: '0.85rem', color: 'rgba(255,255,255,0.7)', margin: 0 }}>
        No commitment — we'll confirm details before your move-in date.
      </p>
    </section>

    {/* HOW IT WORKS */}
    <section style={{ backgroundColor: '#FFF', padding: '3.5rem 2rem' }}>
      <div style={sectionStyle}>
        <h2 style={sectionHeadingStyle}>How It Works</h2>
        <p style={sectionSubStyle}>
          Three steps and your storage is handled. No trips to a facility, no renting a truck, no heavy lifting.
        </p>
        <div style={stepsGrid}>
          <div style={stepCard}>
            <div style={stepNumber}>1</div>
            <div style={stepTitle}>Reserve &amp; Pack</div>
            <div style={stepBody}>
              Choose your unit size, pick a move-in date, and pack your items. We'll
              provide bin guidelines so everything travels safely.
            </div>
          </div>
          <div style={stepCard}>
            <div style={stepNumber}>2</div>
            <div style={stepTitle}>Free Pickup</div>
            <div style={stepBody}>
              A driver comes to your Berkeley address — apartment, dorm, or house — and
              takes everything to a secure local storage facility.
            </div>
          </div>
          <div style={stepCard}>
            <div style={stepNumber}>3</div>
            <div style={stepTitle}>Deliver On Demand</div>
            <div style={stepBody}>
              Need something back? Open the app, select your items, and schedule a
              delivery. We'll bring them back to your door.
            </div>
          </div>
        </div>
      </div>
    </section>

    {/* PRICING */}
    <section style={pricingSection}>
      <h2 style={pricingHeading}>Simple, Affordable Pricing</h2>
      <p style={{ ...pricingSubStyle, textAlign: 'center' }}>
        No hidden fees. Free initial pickup included with every plan.
      </p>
      <div style={pricingGrid}>
        <div style={pricingCard}>
          <div style={pricingLabel}>5×5 Unit — Small Closet</div>
          <div style={pricingPrice}>$30<span style={{ fontSize: '1rem', fontWeight: 400, color: '#AAB5E0' }}>/mo</span></div>
          <div style={pricingDesc}>
            Up to 8 medium boxes, mini fridge, luggage, small appliances, seasonal gear.
            Ideal for decluttering a studio or dorm room.
          </div>
        </div>
        <div style={pricingCard}>
          <div style={pricingLabel}>5×10 Unit — Large Closet</div>
          <div style={pricingPrice}>$50<span style={{ fontSize: '1rem', fontWeight: 400, color: '#AAB5E0' }}>/mo</span></div>
          <div style={pricingDesc}>
            Up to 16 large boxes, desk, bed frame, dresser, TV, small furniture. Great
            for a full apartment move-out or summer break storage.
          </div>
        </div>
      </div>
      <a href="/Download" style={pricingCtaStyle}>Reserve Your Unit</a>
    </section>

    {/* SERVICE AREAS */}
    <section style={areaSection}>
      <h2 style={{ ...sectionHeadingStyle, marginBottom: '0.5rem' }}>Service Areas in Berkeley &amp; the East Bay</h2>
      <p style={{ fontFamily: 'Raleway, sans-serif', fontSize: '1rem', color: '#666', maxWidth: 580, margin: '0 auto' }}>
        We serve the full Berkeley area and nearby East Bay neighborhoods.
      </p>
      <div style={areaGrid}>
        {['Berkeley', 'Southside / UC Berkeley', 'North Berkeley', 'West Berkeley', 'Downtown Berkeley', 'Elmwood', 'Rockridge', 'Richmond', 'El Cerrito', 'Albany'].map((area) => (
          <span key={area} style={areaBadge}>{area}</span>
        ))}
      </div>
    </section>

    {/* FAQ */}
    <section style={faqSection}>
      <h2 style={{ ...sectionHeadingStyle, textAlign: 'left', marginBottom: '1.75rem' }}>Frequently Asked Questions</h2>
      {[
        {
          q: 'Do I need a car or truck to use Bins Storage in Berkeley?',
          a: "No. Bins handles all pickup and delivery. A driver comes to your door — whether you're in an apartment on Telegraph Ave or a house in North Berkeley — picks up your items, and brings them back whenever you need them.",
        },
        {
          q: 'How much does valet storage cost in Berkeley?',
          a: 'Plans start at $30/month for a 5×5 unit (small closet) and $50/month for a 5×10 unit (large closet). Free initial pickup is included with every plan. No hidden fees.',
        },
        {
          q: 'Can I see what I have in storage without visiting the facility?',
          a: 'Yes. The Bins app maintains a photo inventory of everything in storage. You can browse your items and request specific ones to be delivered without ever visiting the facility.',
        },
        {
          q: 'Do you serve UC Berkeley students?',
          a: 'Yes — both on-campus and off-campus pickup. Summer break storage is one of our most popular use cases. See our college storage page for more details.',
        },
        {
          q: 'How far in advance do I need to schedule a pickup or delivery?',
          a: "We ask for at least 48 hours notice for pickups and deliveries. For time-sensitive requests, contact us directly and we'll do our best to accommodate.",
        },
      ].map(({ q, a }) => (
        <div key={q} style={faqItem}>
          <div style={faqQuestion}>{q}</div>
          <div style={faqAnswer}>{a}</div>
        </div>
      ))}
    </section>

    {/* CROSS-LINK to college storage */}
    <section style={crossLinkBanner}>
      <p style={crossLinkText}>
        UC Berkeley student? We have a dedicated storage plan for the end of the semester.
      </p>
      <Link to="/college-storage" style={crossLinkButton}>
        See Student Storage Options →
      </Link>
    </section>
  </>
);

export default Berkeley;
